/**
 * SnapshotButton Component
 *
 * Button that opens a naming modal to create a point-in-time
 * snapshot of the patient's history log.
 * Validates that no open discrepancies exist before allowing creation.
 */

import { useState } from 'react';
import {
  Camera,
  X,
  AlertTriangle,
} from 'lucide-react';
import type { SnapshotType, HistoryLog } from '../../types';

interface SnapshotButtonProps {
  historyLog: HistoryLog;
  onCreateSnapshot: (data: {
    snapshot_name: string;
    snapshot_type: SnapshotType;
    description?: string;
  }) => Promise<void>;
}

const SUGGESTED_NAMES = [
  'Screening Visit',
  'Month 1 Update',
  'Month 3 Update',
  'Month 6 Update',
  'Year 1 Update',
  'Pre-Randomization',
  'End of Treatment',
  'Final Review',
];

export function SnapshotButton({ historyLog, onCreateSnapshot }: SnapshotButtonProps) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [type, setType] = useState<SnapshotType>('manual');
  const [description, setDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasOpenDiscrepancies = historyLog.unresolved_discrepancy_count > 0;

  const handleCreate = async () => {
    if (!name.trim()) return;
    setCreating(true);
    setError(null);
    try {
      await onCreateSnapshot({
        snapshot_name: name.trim(),
        snapshot_type: type,
        description: description.trim() || undefined,
      });
      setOpen(false);
      setName('');
      setDescription('');
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to create snapshot');
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 min-h-[44px] sm:min-h-0"
      >
        <Camera size={16} />
        Snapshot
      </button>

      {/* Modal */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Create Snapshot</h3>
              <button
                onClick={() => setOpen(false)}
                className="p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              >
                <X size={20} />
              </button>
            </div>

            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Create a point-in-time capture of the patient's current medical history.
              Snapshots are immutable and cannot be edited after creation.
            </p>

            {/* Open discrepancies warning */}
            {hasOpenDiscrepancies && (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 mb-4 flex items-start gap-2">
                <AlertTriangle size={16} className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                    {historyLog.unresolved_discrepancy_count} open discrepanc{historyLog.unresolved_discrepancy_count === 1 ? 'y' : 'ies'}
                  </p>
                  <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
                    You can still create a snapshot, but the open discrepancies will be recorded in it.
                  </p>
                </div>
              </div>
            )}

            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-4 text-sm text-red-800 dark:text-red-300">
                {error}
              </div>
            )}

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Snapshot Name *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="e.g., Screening Visit"
                  className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  autoFocus
                />
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {SUGGESTED_NAMES.map(n => (
                    <button
                      key={n}
                      onClick={() => setName(n)}
                      className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-400 rounded-full transition-colors"
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Type
                </label>
                <select
                  value={type}
                  onChange={e => setType(e.target.value as SnapshotType)}
                  className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="manual">Manual Snapshot</option>
                  <option value="milestone">Milestone</option>
                  <option value="pre_review">Pre-Review</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description (optional)
                </label>
                <textarea
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="Brief description of this snapshot..."
                  className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 resize-none bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  rows={2}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setOpen(false)}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors min-h-[44px] sm:min-h-0"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={!name.trim() || creating}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 min-h-[44px] sm:min-h-0"
              >
                <Camera size={16} />
                {creating ? 'Creating...' : 'Create Snapshot'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
